/**
 * Otto Configurator V2 — cadence-first delivery engine.
 * Mirrors delivery-cadence-model.canvas.tsx policy levers (theme-editor driven).
 */
(function (global) {
  var DEFAULT_CADENCES = [
    { key: 'weekly', label: 'Every week', weeks: 1, days: 7 },
    { key: 'biweekly', label: 'Every 2 weeks', weeks: 2, days: 14 },
    { key: 'threeweek', label: 'Every 3 weeks', weeks: 3, days: 21 },
    { key: 'fourweek', label: 'Every 4 weeks', weeks: 4, days: 28 },
    { key: 'fiveweek', label: 'Every 5 weeks', weeks: 5, days: 35 },
    { key: 'sixweek', label: 'Every 6 weeks', weeks: 6, days: 42 },
    { key: 'sevenweek', label: 'Every 7 weeks', weeks: 7, days: 49 },
    { key: 'eightweek', label: 'Every 8 weeks', weeks: 8, days: 56 },
    { key: 'nineweek', label: 'Every 9 weeks', weeks: 9, days: 63 },
    { key: 'tenweek', label: 'Every 10 weeks', weeks: 10, days: 70 },
    { key: 'elevenweek', label: 'Every 11 weeks', weeks: 11, days: 77 },
    { key: 'twelveweek', label: 'Every 12 weeks', weeks: 12, days: 84 }
  ];

  function num(value, fallback) {
    var n = parseFloat(value);
    return isFinite(n) ? n : fallback;
  }

  function parseCadenceWeeks(raw) {
    if (!raw) return null;
    var list = String(raw)
      .split(/[,\s]+/)
      .map(function (x) { return Math.floor(Number(x)); })
      .filter(function (x) { return x > 0 && x <= 52; });
    return list.length ? list : null;
  }

  function buildCadenceList(settings) {
    var enabled = parseCadenceWeeks(settings && settings.cadence_weeks_enabled);
    if (!enabled) return DEFAULT_CADENCES.slice();
    var map = {};
    DEFAULT_CADENCES.forEach(function (c) { map[c.weeks] = c; });
    return enabled
      .map(function (w) { return map[w]; })
      .filter(Boolean);
  }

  function freezerCapLb(settings, profile) {
    var p = String(profile || 'standard').trim().toLowerCase();
    if (p === 'extra') return num(settings.freezer_cap_extra_lb, 72);
    if (p === 'large' || p === 'dedicated') return num(settings.freezer_cap_large_lb, 108);
    return num(settings.freezer_cap_standard_lb, 36);
  }

  function roundDeliveryLb(x, settings) {
    var mode = String((settings && settings.delivery_round_mode) || 'ceil').trim().toLowerCase();
    return mode === 'nearest' ? Math.round(x) : Math.ceil(x);
  }

  function roundDailyIntake(rawDaily, weightLb, settings) {
    var mode = String((settings && settings.daily_round_mode) || 'half').trim().toLowerCase();
    var w = Number(weightLb || 0);
    var x = Number(rawDaily || 0);
    if (!(x > 0)) return 0;

    if (mode === 'portion') {
      var allowTq = !!(settings && settings.allow_three_quarter_daily);
      if (x <= 0.5) return 0.5;
      if (x <= 1) return 1;
      if (allowTq && x <= 1.5) return 1.5;
      return Math.ceil(x);
    }

    var step = 2;
    if (mode === 'quarter') step = 4;
    else if (mode === 'hybrid') {
      var hybridMax = num(settings.hybrid_small_dog_max_lb, 30);
      step = w <= hybridMax ? 4 : 2;
    }
    return Math.round(x * step) / step;
  }

  function bagSplit(amount, cap) {
    amount = Math.max(0, Math.floor(Number(amount || 0)));
    cap = Math.max(1, Math.floor(Number(cap || 0)));
    if (!amount) return { count: 0, sizes: [] };
    var count = Math.ceil(amount / cap);
    var base = Math.floor(amount / count);
    var rem = amount - base * count;
    var sizes = [];
    for (var i = 0; i < count; i++) sizes.push(base + (i < rem ? 1 : 0));
    return { count: count, sizes: sizes };
  }

  function evaluateCadence(dailyLb, settings, freezerProfile) {
    var minLb = num(settings.min_delivery_lb, 18);
    var maxDays = num(settings.max_delivery_interval_days, 70);
    var cap = freezerCapLb(settings, freezerProfile);
    var bagCap = num(settings.bag_capacity_lb, 20);
    var cadences = buildCadenceList(settings);
    var daily = Math.max(0, Number(dailyLb || 0));

    var buckets = cadences.map(function (c) {
      var rawAmount = daily * c.days;
      var amount = roundDeliveryLb(rawAmount, settings);
      var reasons = [];
      if (amount < minLb) reasons.push('min');
      if (c.days > maxDays) reasons.push('max');
      var weeklyExempt = settings.weekly_exempt_from_cap !== false;
      if (amount > cap && !(c.key === 'weekly' && weeklyExempt)) reasons.push('cap');
      var split = bagSplit(amount, bagCap);
      return {
        key: c.key,
        label: c.label,
        weeks: c.weeks,
        days: c.days,
        rawAmount: rawAmount,
        lbs: amount,
        feasible: reasons.length === 0,
        reasons: reasons,
        bags: split.count,
        bagSizes: split.sizes
      };
    });

    var feasible = buckets.filter(function (b) { return b.feasible; });
    var recommended = feasible.length
      ? feasible.reduce(function (a, b) { return b.days > a.days ? b : a; })
      : null;

    var status = 'ok';
    if (!recommended && daily > 0) {
      var longest = buckets[buckets.length - 1];
      if (longest && longest.lbs < minLb) status = 'small';
    }

    return {
      daily: daily,
      buckets: buckets,
      feasible: feasible,
      recommended: recommended,
      status: status,
      minLb: minLb,
      maxDays: maxDays,
      freezerCapLb: cap
    };
  }

  function applySmallDogPolicy(ev, settings) {
    if (ev.feasible.length || ev.status !== 'small') return ev.feasible.slice();
    var policy = String((settings && settings.small_dog_policy) || 'soft-floor').trim().toLowerCase();
    var longest = ev.buckets.length ? ev.buckets[ev.buckets.length - 1] : null;
    if (!longest) return [];

    if (policy === 'soft-floor' && longest.reasons.indexOf('min') >= 0 && longest.reasons.length === 1) {
      return [longest];
    }
    if (policy === 'overship') {
      var bagCap = num(settings.bag_capacity_lb, 20);
      var split = bagSplit(ev.minLb, bagCap);
      return [{
        key: longest.key,
        label: longest.label,
        weeks: longest.weeks,
        days: longest.days,
        lbs: ev.minLb,
        feasible: true,
        reasons: [],
        bags: split.count,
        bagSizes: split.sizes
      }];
    }
    return [];
  }

  /**
   * Display policy (mirrors otto-engine.js): a small ladder of rungs
   * (1/2/4/6/8 weeks); the minimum viable cadence rounds UP to the nearest
   * rung. Cadences past 8 weeks only appear as a single option when they ARE
   * the minimum.
   *
   *   min 1 -> 1,2,4,6,8 · min 2 -> 2,4,6,8 · min 3-4 -> 4,6,8
   *   min 5-6 -> 6,8 · min 7-8 -> 8 · min 9-12 -> single option
   */
  var CADENCE_DISPLAY_MAX_WEEKS = 8;
  var CADENCE_DISPLAY_RUNG_WEEKS = [1, 2, 4, 6, 8];

  function curateCadenceChoices(options) {
    var list = (options || []).slice().sort(function (a, b) { return a.weeks - b.weeks; });
    if (list.length <= 1) return list;
    var minWeeks = list[0].weeks;
    if (minWeeks > CADENCE_DISPLAY_MAX_WEEKS) return [list[0]];
    var floor = null;
    CADENCE_DISPLAY_RUNG_WEEKS.forEach(function (w) {
      if (floor != null || w < minWeeks) return;
      if (list.some(function (o) { return o.weeks === w; })) floor = w;
    });
    if (floor == null) return [list[0]];
    return list.filter(function (o) {
      return o.weeks >= floor && CADENCE_DISPLAY_RUNG_WEEKS.indexOf(o.weeks) >= 0;
    });
  }

  function getCadenceOptions(dailyLb, settings, freezerProfile) {
    var ev = evaluateCadence(dailyLb, settings, freezerProfile);
    var chosen = ev.feasible.length ? ev.feasible : applySmallDogPolicy(ev, settings);
    return curateCadenceChoices(chosen).map(function (b) {
      return {
        key: b.key,
        label: b.label,
        weeks: b.weeks,
        days: b.days,
        lbs: b.lbs,
        bags: b.bags,
        bagSizes: b.bagSizes
      };
    });
  }

  global.OttoCfgV2Cadence = {
    DEFAULT_CADENCES: DEFAULT_CADENCES,
    buildCadenceList: buildCadenceList,
    freezerCapLb: freezerCapLb,
    roundDeliveryLb: roundDeliveryLb,
    roundDailyIntake: roundDailyIntake,
    bagSplit: bagSplit,
    evaluateCadence: evaluateCadence,
    getCadenceOptions: getCadenceOptions,
    curateCadenceChoices: curateCadenceChoices
  };
})(typeof window !== 'undefined' ? window : this);
